import React, { useState } from 'react';
import styled from '@emotion/styled';
import { usePlacesAutocomplete } from '../../hooks/usePlacesAutoComplete';
import { ClickTargets } from 'rwl-shared/dist/models/analytics';
import { getPageUrl, useAnalyticsClickEvent, mqDown } from 'rwl-ui';
import { formatZipCode } from './utils';

const GateOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: #ffffffad;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    padding-top: 120px;
    z-index: 99999; /* Above everything always */

    ${mqDown('sm')} {
        padding-top: 60px;
        padding-left: 20px;
        padding-right: 20px;
        background-color: #fff;
    }
`;

const GateContent = styled.div`
    width: 100%;
    text-align: center;
    padding-top: 10%;
    padding-bottom: 10%;
    background: radial-gradient(
        ellipse at 50% 50%,
        #ffffff97 20%,
        rgba(255, 255, 255, 0) 100%
    );

    ${mqDown('sm')} {
        padding-top: 20px;
        padding-bottom: 20px;
    }
`;

const Title = styled.div<{ $tabColor?: string }>`
    font-size: 36px;
    font-weight: 700;
    line-height: 150%;
    margin-bottom: 8px;
    color: var(--rwl-text-default);

    ${mqDown('sm')} {
        font-size: 28px;
        margin-bottom: 12px;
    }
`;

const Subtitle = styled.div`
    font-weight: 500;
    font-size: 20px;
    line-height: 150%;
    color: var(--rwl-text-default);
    margin-bottom: var(--rwl-spacing-md);

    ${mqDown('sm')} {
        font-size: 16px;
        margin-bottom: 24px;
        padding: 0 10px;
    }
`;

const InputWrapper = styled.div<{ $tabColor?: string }>`
    display: flex;
    margin-bottom: var(--rwl-spacing-md);
    max-width: 500px;
    margin-left: auto;
    margin-right: auto;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
    border: 2px solid ${(props) => props.$tabColor || '#e8e8e8'};

    ${mqDown('sm')} {
        margin-bottom: 20px;
        margin-left: 10px;
        margin-right: 10px;
        flex-direction: row;
    }
`;

const Input = styled.input`
    flex: 1;
    padding: 16px 20px;
    border: none;
    font-size: 16px;
    color: #555;

    &::placeholder {
        color: #999;
    }

    &:focus {
        outline: none;
    }

    ${mqDown('sm')} {
        padding: 18px 20px;
        font-size: 16px;
        border-bottom: 1px solid #e8e8e8;
    }
`;

const Button = styled.button<{ $tabColor?: string }>`
    background-color: ${(props) => props.$tabColor || '#e47500'};
    color: white;
    border: none;
    min-width: 80px;
    font-size: 18px;
    font-weight: 600;
    cursor: pointer;
    transition: filter 0.2s;

    &:hover {
        filter: brightness(0.9);
    }

    &:disabled {
        background-color: #cccccc;
        cursor: not-allowed;
    }

    ${mqDown('sm')} {
        padding: 18px 20px;
        font-size: 18px;
        min-height: 56px; /* Better touch target */
    }
`;

const ErrorText = styled.div`
    color: #e53935;
    font-size: 14px;
    font-weight: 400;
    line-height: 150%;
    min-height: 20px;
`;

interface DirectGateProps {
    onClose: () => void;
    tabBackgroundColor?: string;
}

const DirectGate = ({ onClose, tabBackgroundColor }: DirectGateProps) => {
    const [zipCode, setZipCode] = useState('');
    const [error, setError] = useState('');
    const { setSelectedPlaceId } = usePlacesAutocomplete();
    const sendClickEvent = useAnalyticsClickEvent();

    const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const formattedValue = formatZipCode(e.target.value);
        setZipCode(formattedValue);
        if (error) {
            setError('');
        }
    };

    const handleSubmit = () => {
        if (!zipCode) {
            setError('Please enter a ZIP code');
            return;
        }
        sendClickEvent({
            url: getPageUrl(),
            target: ClickTargets.SEARCH_FINISHED,
        });

        // Remove the dash for geocoding
        const cleanZipCode = zipCode.replace('-', '');

        // Use Geocoding API to get place_id from ZIP code
        const geocoder = new google.maps.Geocoder();
        geocoder.geocode(
            {
                address: cleanZipCode,
                componentRestrictions: {
                    country: 'US',
                    postalCode: cleanZipCode,
                },
            },
            (results, status) => {
                if (
                    status === google.maps.GeocoderStatus.OK &&
                    results &&
                    results.length > 0
                ) {
                    const placeId = results[0].place_id;
                    setSelectedPlaceId(placeId);
                    sendClickEvent({
                        url: getPageUrl(),
                        target: ClickTargets.SEARCH_FINISHED,
                    });
                    onClose();
                } else {
                    setError('ZIP code not found. Please try another.');
                }
            }
        );
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSubmit();
        }
    };

    return (
        <GateOverlay data-testid="zip-gate-overlay">
            <GateContent>
                <Title $tabColor={tabBackgroundColor}>
                    Explore our local projects
                </Title>
                <Subtitle>
                    {` Enter your ZIP code to see work we've completed right in
                    your neighborhood.`}
                </Subtitle>
                <InputWrapper $tabColor={tabBackgroundColor}>
                    <Input
                        type="text"
                        placeholder="Enter your ZIP code"
                        value={zipCode}
                        onChange={handleZipChange}
                        onKeyDown={handleKeyDown}
                        autoFocus
                        data-testid="zip-input"
                    />
                    <Button
                        onClick={handleSubmit}
                        data-testid="go-button"
                        $tabColor={tabBackgroundColor}
                    >
                        Go
                    </Button>
                </InputWrapper>
                <ErrorText>{error}</ErrorText>
            </GateContent>
        </GateOverlay>
    );
};

export default DirectGate;
